import React, { useEffect, useState } from "react";
import { Users, Save, ExternalLink, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { upsertAdvisorListing, fetchMyListing } from "@/lib/api";
import { toast } from "sonner";
import { errorMessage } from "@/lib/errorMessage";

const EMPTY = {
  display_name: "",
  tagline: "",
  website_url: "",
  logo_url: "",
  bio: "",
  location: "",
};

export default function ListingForm({ token, packageId }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [expiresAt, setExpiresAt] = useState(null);

  useEffect(() => {
    if (!token || packageId !== "advisor_listing_yearly") {
      setLoading(false);
      return;
    }
    let active = true;
    fetchMyListing(token)
      .then((r) => {
        if (!active) return;
        if (r?.listing) {
          setForm({ ...EMPTY, ...r.listing });
        }
        setExpiresAt(r?.expires_at);
      })
      .catch((e) => console.warn("[Centennial] listing load failed:", e))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [token, packageId]);

  if (packageId !== "advisor_listing_yearly") return null;

  const set = (k, v) => setForm({ ...form, [k]: v });

  const save = async () => {
    if (!form.display_name || !form.website_url) {
      toast.error("Display name and website URL are required.");
      return;
    }
    setSaving(true);
    try {
      const r = await upsertAdvisorListing({ token, ...form });
      toast.success(`Listing saved. Visible at /featured for the next ~${Math.ceil((r.expires_at - Date.now() / 1000) / 86400)} days.`);
    } catch (e) {
      const detail = errorMessage(e, "save failed");
      toast.error(detail);
    } finally {
      setSaving(false);
    }
  };

  const expiryDate = expiresAt ? new Date(expiresAt * 1000).toLocaleDateString() : "—";

  if (loading) {
    return (
      <div className="bg-white border border-[#E5E2DC] rounded-2xl p-6 text-sm text-[#5C6B62] inline-flex items-center gap-2">
        <Loader2 className="animate-spin" size={14} /> loading your listing…
      </div>
    );
  }

  return (
    <div data-testid="listing-form" className="bg-white border border-[#E5E2DC] rounded-2xl p-6 sm:p-8">
      <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
        <div>
          <div className="text-[11px] tracking-[0.22em] uppercase text-[#5C6B62] font-semibold inline-flex items-center gap-2">
            <Users size={12} /> featured advisor listing
          </div>
          <div className="font-display text-lg text-[#1C2B23] mt-1">Your public profile</div>
        </div>
        <a
          href="/featured"
          target="_blank"
          rel="noreferrer"
          className="text-xs text-[#2B5339] hover:underline inline-flex items-center gap-1"
        >
          View /featured <ExternalLink size={11} />
        </a>
      </div>
      <p className="text-[12px] text-[#5C6B62] mb-5">
        Active through {expiryDate}. Edits go live immediately — no review queue.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="md:col-span-2">
          <Label className="text-[10px] tracking-[0.2em] uppercase text-[#5C6B62] font-semibold">Display name *</Label>
          <Input
            data-testid="listing-display-name"
            value={form.display_name}
            onChange={(e) => set("display_name", e.target.value)}
            placeholder="Jane Smith, CFP"
            maxLength={80}
            className="mt-2"
          />
        </div>
        <div className="md:col-span-2">
          <Label className="text-[10px] tracking-[0.2em] uppercase text-[#5C6B62] font-semibold">Tagline</Label>
          <Input
            data-testid="listing-tagline"
            value={form.tagline}
            onChange={(e) => set("tagline", e.target.value)}
            placeholder="Fee-only fiduciary helping FIRE-aspiring families"
            maxLength={140}
            className="mt-2"
          />
        </div>
        <div>
          <Label className="text-[10px] tracking-[0.2em] uppercase text-[#5C6B62] font-semibold">Website URL *</Label>
          <Input
            data-testid="listing-website"
            value={form.website_url}
            onChange={(e) => set("website_url", e.target.value)}
            placeholder="https://yoursite.com"
            className="mt-2 font-mono-num text-sm"
          />
        </div>
        <div>
          <Label className="text-[10px] tracking-[0.2em] uppercase text-[#5C6B62] font-semibold">Location</Label>
          <Input
            data-testid="listing-location"
            value={form.location}
            onChange={(e) => set("location", e.target.value)}
            placeholder="Austin, TX"
            className="mt-2"
          />
        </div>
        <div className="md:col-span-2">
          <Label className="text-[10px] tracking-[0.2em] uppercase text-[#5C6B62] font-semibold">Logo / headshot URL</Label>
          <Input
            data-testid="listing-logo"
            value={form.logo_url}
            onChange={(e) => set("logo_url", e.target.value)}
            placeholder="https://yoursite.com/photo.jpg"
            className="mt-2 font-mono-num text-xs"
          />
        </div>
        <div className="md:col-span-2">
          <Label className="text-[10px] tracking-[0.2em] uppercase text-[#5C6B62] font-semibold">Bio (max 600 chars)</Label>
          <textarea
            data-testid="listing-bio"
            value={form.bio}
            onChange={(e) => set("bio", e.target.value.slice(0, 600))}
            rows={4}
            className="mt-2 w-full text-sm rounded-lg border border-[#E5E2DC] bg-white focus:border-[#2B5339] focus:outline-none p-3 leading-relaxed"
            placeholder="Briefly: who you serve, your approach, your credentials."
          />
          <div className="mt-1 text-[10px] text-[#5C6B62] text-right">{form.bio.length}/600</div>
        </div>
      </div>

      <button
        data-testid="listing-save"
        type="button"
        onClick={save}
        disabled={saving}
        className="mt-5 bg-[#2B5339] text-white hover:bg-[#1E3B28] disabled:opacity-60 rounded-full px-6 py-2.5 text-sm inline-flex items-center gap-2"
      >
        {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
        {saving ? "Saving…" : "Save listing"}
      </button>
    </div>
  );
}
